import { Plus, X } from "lucide-react";

const ROLES = [
  { value: "customer_service", label: "Customer Service" },
  { value: "booking", label: "Booking / Appointments" },
  { value: "faq", label: "FAQ / Info" },
  { value: "sales", label: "Sales" },
  { value: "both", label: "Both (Support + Booking)" },
];

const TONE_PRESETS = [
  "friendly and professional",
  "warm and casual",
  "expert and concise",
  "playful and helpful",
];

import { AGENTS } from "@/constants/testIds";

/** All three form cards: Identity · Personality · Business knowledge.
 * State lives in the parent; this component is purely presentational. */
export function AgentFormPanel({
  form, update,
  newService, setNewService, addService, removeService,
  newFaqQ, setNewFaqQ, newFaqA, setNewFaqA, addFaq, removeFaq,
}) {
  return (
    <div className="lg:col-span-3 space-y-6">
      {/* Identity */}
      <div className="bg-white border border-neutral-200 p-6">
        <div className="overline mb-4">Identity</div>
        <label className="overline block mb-2">Agent name</label>
        <input
          data-testid={AGENTS.nameInput}
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 mb-4 text-sm focus:outline-none focus:border-black"
          placeholder="e.g. Rapid Response Assistant"
        />
        <label className="overline block mb-2">Tagline</label>
        <input
          value={form.tagline}
          onChange={(e) => update("tagline", e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 mb-4 text-sm focus:outline-none focus:border-black"
          placeholder="Rapid Response Plumbing · Assistant"
        />
        <label className="overline block mb-2">Greeting message</label>
        <input
          data-testid={AGENTS.greetingInput}
          value={form.greeting}
          onChange={(e) => update("greeting", e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
          placeholder="Hi! How can we help today?"
        />
      </div>

      {/* Personality */}
      <div className="bg-white border border-neutral-200 p-6">
        <div className="overline mb-4">Personality</div>
        <label className="overline block mb-2">Primary role</label>
        <select
          data-testid={AGENTS.roleSelect}
          value={form.role}
          onChange={(e) => update("role", e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 mb-4 text-sm focus:outline-none focus:border-black bg-white"
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <label className="overline block mb-2">Tone</label>
        <input
          data-testid={AGENTS.toneInput}
          value={form.tone}
          onChange={(e) => update("tone", e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 mb-2 text-sm focus:outline-none focus:border-black"
        />
        <div className="flex flex-wrap gap-2 mb-4">
          {TONE_PRESETS.map((t) => (
            <button key={t} onClick={() => update("tone", t)} className="text-xs px-2 py-1 border border-neutral-300 hover:bg-neutral-100">
              {t}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            data-testid={AGENTS.bookingToggle}
            type="checkbox"
            checked={form.booking_enabled}
            onChange={(e) => update("booking_enabled", e.target.checked)}
            className="w-4 h-4"
          />
          Enable appointment booking
        </label>
      </div>

      {/* Business knowledge */}
      <div className="bg-white border border-neutral-200 p-6">
        <div className="overline mb-4">Business knowledge</div>
        <label className="overline block mb-2">Business description</label>
        <textarea
          data-testid={AGENTS.businessInfoInput}
          value={form.business_info}
          onChange={(e) => update("business_info", e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-neutral-300 mb-4 text-sm font-mono focus:outline-none focus:border-black"
          placeholder="Tell the agent about the business — what you do, service area, years in business, etc."
        />
        <label className="overline block mb-2">Business hours</label>
        <input
          data-testid={AGENTS.hoursInput}
          value={form.business_hours}
          onChange={(e) => update("business_hours", e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 mb-4 text-sm focus:outline-none focus:border-black"
          placeholder="Mon-Fri 8am-6pm, Sat 9am-2pm"
        />

        <label className="overline block mb-2">Services offered</label>
        <div className="flex gap-2 mb-3">
          <input
            data-testid={AGENTS.servicesInput}
            value={newService}
            onChange={(e) => setNewService(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addService())}
            className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
            placeholder="e.g. Emergency plumbing repair"
          />
          <button onClick={addService} type="button" className="lf-btn-outline text-sm px-3">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {form.services.map((s, i) => (
            <span key={i} className="pill pill-new inline-flex items-center gap-2">
              {s}
              <button onClick={() => removeService(i)} className="hover:text-red-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>

        <label className="overline block mb-2">FAQ pairs</label>
        <div className="space-y-2 mb-3">
          {form.faqs.map((f, i) => (
            <div key={i} className="border border-neutral-200 p-3 flex justify-between items-start gap-2">
              <div className="flex-1">
                <div className="text-xs font-semibold">Q: {f.q}</div>
                <div className="text-xs text-neutral-600 mt-1">A: {f.a}</div>
              </div>
              <button onClick={() => removeFaq(i)} className="text-neutral-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input
            data-testid={AGENTS.faqQuestionInput}
            value={newFaqQ}
            onChange={(e) => setNewFaqQ(e.target.value)}
            className="px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
            placeholder="Question (e.g. Do you do emergency calls?)"
          />
          <input
            data-testid={AGENTS.faqAnswerInput}
            value={newFaqA}
            onChange={(e) => setNewFaqA(e.target.value)}
            className="px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
            placeholder="Answer"
          />
        </div>
        <button
          data-testid={AGENTS.faqAddBtn}
          onClick={addFaq}
          type="button"
          className="lf-btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add FAQ
        </button>
      </div>
    </div>
  );
}
